/** Vazio em dev: o proxy do Vite encaminha /api e o cookie fica same-origin. */
const BASE = import.meta.env.VITE_API_URL ?? '';

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    mensagem: string,
    readonly detalhes?: Record<string, unknown>,
  ) {
    super(mensagem);
    this.name = 'ErroApi';
  }

  /**
   * Apelido de `message`. O resto do código é em português e ler
   * `erro.mensagem` evita a troca de idioma no meio da expressão.
   */
  get mensagem(): string {
    return this.message;
  }
}

type Opcoes = { metodo?: 'GET' | 'POST' | 'PATCH' | 'DELETE'; corpo?: unknown };

export async function api<T>(caminho: string, { metodo = 'GET', corpo }: Opcoes = {}): Promise<T> {
  let resposta: Response;

  try {
    resposta = await fetch(`${BASE}${caminho}`, {
      method: metodo,
      // O cookie de sessão do better-auth é httpOnly: sem isto nenhuma rota
      // autenticada funciona.
      credentials: 'include',
      headers: corpo ? { 'Content-Type': 'application/json' } : undefined,
      body: corpo ? JSON.stringify(corpo) : undefined,
    });
  } catch {
    throw new ErroApi(0, 'rede', 'Não foi possível conectar. Verifique sua internet.');
  }

  if (resposta.status === 204) return undefined as T;

  const dados = await resposta.json().catch(() => null);

  if (!resposta.ok) {
    throw new ErroApi(
      resposta.status,
      dados?.erro ?? 'desconhecido',
      dados?.mensagem ?? 'Algo deu errado. Tente novamente.',
      dados?.detalhes,
    );
  }

  return dados as T;
}

/** Formata "1234.50" como "R$ 1.234,50". */
export function moeda(valor: string | number): string {
  return Number(valor).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/** Taxa da maquininha em débito ou crédito. Pix e dinheiro não pagam. */
export const TAXA_CARTAO = 0.03;

/** Quanto o acampamento custa passando no cartão, arredondado ao centavo. */
export function valorNoCartao(valor: string | number): number {
  return Math.round(Number(valor) * (1 + TAXA_CARTAO) * 100) / 100;
}

export function data(valor: string | Date | null | undefined): string {
  if (!valor) return '—';
  return new Date(valor).toLocaleDateString('pt-BR');
}

export function dataHora(valor: string | Date | null | undefined): string {
  if (!valor) return '—';
  return new Date(valor).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/** O banco guarda só dígitos; a exibição é formatada aqui. */
export function telefone(digitos: string | null | undefined): string {
  if (!digitos) return '—';
  const d = digitos.replace(/\D/g, '');
  if (d.length !== 11) return digitos;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/** Máscara progressiva, aplicada enquanto o responsável digita. */
export function mascararTelefone(entrada: string): string {
  const d = entrada.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export function mascararCpf(entrada: string): string {
  const d = entrada.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export function cpf(digitos: string | null | undefined): string {
  if (!digitos) return '—';
  return mascararCpf(digitos);
}

/**
 * Confere os dígitos verificadores, não só o comprimento.
 *
 * Mesma regra do backend (`lib/texto.ts`), repetida aqui de propósito: são
 * repositórios separados, então não há como importar. Aqui serve para
 * mostrar o erro no campo enquanto a pessoa digita; quem recusa a ficha de
 * fato é o servidor. Se um dia a regra mudar, mude nos dois.
 */
export function cpfValido(entrada: string): boolean {
  const d = entrada.replace(/\D/g, '');
  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false;

  for (const [ate, posicao] of [
    [9, 10],
    [10, 11],
  ] as const) {
    let soma = 0;
    for (let i = 0; i < ate; i++) soma += Number(d[i]) * (posicao - i);
    const resto = (soma * 10) % 11;
    if ((resto === 10 ? 0 : resto) !== Number(d[ate])) return false;
  }
  return true;
}
